import React, { useMemo, useState, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Typography, Tag, Badge } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  BarChartOutlined,
  SettingOutlined,
  LogoutOutlined,
  PlusOutlined,
  ShopOutlined,
  BellOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  ApiOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  PictureOutlined,
  AimOutlined,
  WechatOutlined,
  RobotOutlined,
  CarOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './SidebarStyles.css';

const { Sider } = Layout;
const { Text } = Typography;

function AgencySidebar({ collapsed, onCollapse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useAuth();
  const [openKeys, setOpenKeys] = useState([]);

  const computedAvatar = useMemo(() => {
    const u = currentUser || {};
    return u.profile_picture_url || u.photoURL || u.avatar_url || u.image_url || null;
  }, [currentUser]);

  const [avatarUrl, setAvatarUrl] = useState(null);
  useEffect(() => {
    if (computedAvatar && /^\//.test(computedAvatar)) {
      setAvatarUrl(window.location.origin + computedAvatar);
    } else {
      setAvatarUrl(computedAvatar);
    }
  }, [computedAvatar]);

  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getSelectedKeys = () => {
    const found = menuItems.find((item) => item.path === location.pathname)
      || menuItems.flatMap((i) => i.children || []).find((i) => i.path === location.pathname);
    return found ? [found.key] : [];
  };

  const menuItems = useMemo(() => [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      path: '/agency/dashboard'
    },
    {
      key: 'workspaces',
      icon: <ShopOutlined />,
      label: 'Locations',
      path: '/agency/workspaces'
    },
    {
      key: 'vehicles',
      icon: <CarOutlined />,
      label: 'Vehicle Management',
      path: '/agency/vehicles'
    },
    {
      key: 'clients',
      icon: <TeamOutlined />,
      label: 'Clients',
      path: '/agency/clients'
    },
    {
      key: 'campaigns',
      icon: <BarChartOutlined />,
      label: 'Campaigns',
      path: '/agency/campaigns'
    },
    {
      key: 'new-campaign',
      icon: <PlusOutlined />,
      label: 'New Campaign',
      path: '/agency/new-campaign'
    },
    {
      key: 'team',
      icon: <TeamOutlined />,
      label: 'Team',
      path: '/agency/team'
    },
    {
      key: 'ad-creatives',
      icon: <PictureOutlined />,
      label: 'Ad Creatives',
      path: '/agency/creatives'
    },
    {
      key: 'audience',
      icon: <AimOutlined />,
      label: 'Audience & Targeting',
      path: '/agency/audience'
    },
    {
      key: 'budgets',
      icon: <CalendarOutlined />,
      label: 'Budgets & Scheduling',
      path: '/agency/budgets'
    },
    {
      key: 'analytics',
      icon: <BarChartOutlined />,
      label: 'Analytics & Reports',
      path: '/agency/analytics'
    },

    {
      key: 'team',
      icon: <TeamOutlined />,
      label: 'Team Management',
      path: '/agency/team'
    },
    {
      key: 'integrations',
      icon: <ApiOutlined />,
      label: 'Integrations',
      path: '/agency/integrations'
    },
    {
      key: 'billing',
      icon: <CreditCardOutlined />,
      label: 'Billing & Plans',
      path: '/agency/billing'
    },
    {
      key: 'notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
      path: '/agency/notifications'
    },
    {
      key: 'ai-tools',
      icon: <RobotOutlined />,
      label: 'AI Tools',
      path: '/agency/ai-tools'
    },
    {
      key: 'support',
      icon: <QuestionCircleOutlined />,
      label: 'Support',
      path: '/agency/support'
    },
    {
      key: 'agency-chat',
      icon: <WechatOutlined />,
      label: 'Chat Room',
      path: '/agency/chat'
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      path: '/agency/settings'
    }
  ], []);

  // Flatten items for collapsed mode so every item (including children) appears as an icon with tooltip
  const flattenedItems = useMemo(() => {
    const flat = [];
    const walk = (items, parentIcon = null) => {
      items.forEach((it) => {
        const base = {
          key: it.key,
          icon: it.icon || parentIcon || <FileTextOutlined />,
          label: it.label,
          title: typeof it.label === 'string' ? it.label : undefined,
          path: it.path
        };
        if (it.children && it.children.length) {
          if (it.path) flat.push(base);
          walk(it.children, it.icon || parentIcon);
        } else {
          flat.push(base);
        }
      });
    };
    walk(menuItems);
    return flat.map(({ key, icon, label, title }) => ({ key, icon, label, title }));
  }, [menuItems]);

  // Expanded mode items with badges and child classes preserved
  const expandedItems = useMemo(() => (
    menuItems.map((item) => ({
      ...item,
      icon: (
        <>
          {item.icon}
          {item.badge ? (
            <Badge
              count={item.badge}
              size="small"
              style={{ marginLeft: 8 }}
            />
          ) : null}
        </>
      ),
      children: item.children?.map((child) => ({
        ...child,
        icon: child.icon || null,
        className: 'sidebar-submenu-item'
      }))
    }))
  ), [menuItems]);

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      className="sidebar-container"
      width={250}
      collapsedWidth={80}
    >
      <div className="sidebar-header">
        <div className="logo-container" style={{ 
          padding: collapsed ? '12px 8px' : '16px', 
          textAlign: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <img 
            src="/SidebarLogo.png" 
            alt="PathMarkr Logo" 
            style={{ 
              height: 'auto',
              maxHeight: collapsed ? '40px' : '50px', 
              maxWidth: '100%', 
              width: 'auto',
              objectFit: 'contain',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => onCollapse(!collapsed)}
          />
          {!collapsed && (
            <div style={{ marginTop: '8px' }}>
              <Text strong style={{ fontSize: '14px', color: '#1f2937' }}>
                Enterprise Portal
              </Text>
            </div>
          )}
        </div>
      </div>

      {/* User Info (from AuthContext) */}
      {!collapsed && (
        <div
          style={{
            padding: '24px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="avatar"
              onError={() => { setAvatarUrl(null); }}
              style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '1px solid #f0f0f0', marginBottom: 8 }}
            />
          ) : (
            <Avatar
              size={80}
              style={{
                backgroundColor: '#ff4d4f',
                fontSize: '32px',
                fontWeight: 'bold',
                marginBottom: 8
              }}
            >
              {(() => {
                const name = (currentUser?.full_name || currentUser?.name || currentUser?.email || '').trim();
                if (!name) return 'U';
                const parts = name.split(/\s+/).filter(Boolean);
                const initials = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
                return initials.toUpperCase() || 'U';
              })()}
            </Avatar>
          )}
          <div style={{ marginTop: '8px', textAlign: 'center' }}>
            <Text strong style={{ display: 'block', color: 'var(--black)' }}>
              {currentUser?.full_name || currentUser?.name || currentUser?.email || ''}
            </Text>
            {currentUser?.email && (
              <Text style={{ fontSize: '12px', color: 'var(--muted-gray)', display: 'block' }}>
                {currentUser.email}
              </Text>
            )}
            <Tag color="green" style={{ marginTop: '8px', fontSize: '11px' }}>
              ENTERPRISE (MULTI-LOCATION)
            </Tag>
          </div>
        </div>
      )}

      {/* Menu (scrollable container) */}
      <div className="sidebar-menu-wrapper">
        <Menu
          theme="light"
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={getSelectedKeys()}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          className="sidebar-menu"
          onClick={({ key }) => {
            const item =
              menuItems.find((i) => i.key === key) ||
              menuItems.flatMap((i) => i.children || []).find((i) => i.key === key);
            if (item?.path) {
              if (key === 'logout') {
                handleLogout();
              } else {
                navigate(item.path);
              }
            }
          }}
          items={collapsed ? flattenedItems : expandedItems}
          style={{
            borderRight: 0,
            background: 'transparent',
            marginTop: '16px'
          }}
        />
      </div>

      {/* Footer */}
      <div className="sidebar-footer" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '8px 12px',
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        background: 'transparent'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Button
            type="text"
            className="sidebar-action sidebar-action--danger"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            danger
          >
            {!collapsed && 'Logout'}
          </Button>
        </div>
        {!collapsed && (
          <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--muted-gray)', whiteSpace: 'nowrap' }}>
            v1.4.7 Alpha | © 2026 PathMarkr
          </div>
        )}
      </div>
    </Sider>
  );
}

export default AgencySidebar;
